import { invokeWithSingleContextHeal } from "@wakeloop/adapter-core";
import { isLikelyContextLostError } from "../context/codex-context-heal.js";
import { invokeCodexAppServerTransport } from "../transport/codex-app-server-transport.js";
import { invokeCodexCliTransport } from "../transport/codex-cli-transport.js";
export class CodexRuntimeError extends Error {
    code;
    errorType;
    constructor(input) {
        super(input.message);
        this.name = "CodexRuntimeError";
        this.code = input.code;
        this.errorType = input.errorType;
    }
}
export async function invokeCodexWithSingleContextHeal(input) {
    const result = await invokeWithSingleContextHeal({
        contextId: input.threadId,
        createRebuildFailedError: (error) => new CodexRuntimeError({
            code: "context.rebuild_failed",
            errorType: "adapter",
            message: `context.rebuild_failed: ${toErrorMessage(error)}`,
        }),
        invokeOnce: (threadId) => invokeCodexOnce({
            agentControllerPermissionMode: input.agentControllerPermissionMode,
            launchContract: input.launchContract,
            codexTimeoutMs: input.codexTimeoutMs,
            codexWorkdir: input.codexWorkdir,
            conversationPolicy: input.conversationPolicy,
            env: input.env,
            executionMode: input.executionMode,
            ...(input.onProcessControlEvent
                ? { onProcessControlEvent: input.onProcessControlEvent }
                : {}),
            ...(input.onPreview ? { onPreview: input.onPreview } : {}),
            ...(input.onRuntimeEvent
                ? { onRuntimeEvent: input.onRuntimeEvent }
                : {}),
            prompt: input.prompt,
            streamingMode: input.streamingMode,
            threadId,
        }).then((value) => ({
            contextId: value.threadId,
            output: value.output,
            ...(value.telemetry ? { telemetry: value.telemetry } : {}),
        })),
        isContextLostError: (error) => isLikelyContextLostError(error instanceof Error ? error.message : String(error)),
        onContextLost: input.onContextLost,
    });
    return {
        contextHealed: result.contextHealed,
        output: result.output,
        ...(result.telemetry ? { telemetry: result.telemetry } : {}),
        threadId: result.contextId,
    };
}
export async function invokeCodexAppServer(input) {
    try {
        return await invokeCodexAppServerTransport({
            agentControllerPermissionMode: input.agentControllerPermissionMode,
            launchContract: input.launchContract,
            codexTimeoutMs: input.codexTimeoutMs,
            codexWorkdir: input.codexWorkdir,
            conversationPolicy: input.conversationPolicy,
            env: input.env,
            executionMode: input.executionMode,
            ...(input.onProcessControlEvent
                ? { onProcessControlEvent: input.onProcessControlEvent }
                : {}),
            ...(input.onActiveTurn ? { onActiveTurn: input.onActiveTurn } : {}),
            ...(input.onPreview ? { onPreview: input.onPreview } : {}),
            ...(input.onRuntimeEvent ? { onRuntimeEvent: input.onRuntimeEvent } : {}),
            prompt: input.prompt,
            streamingMode: input.streamingMode,
            taskId: input.taskId,
            threadId: input.threadId,
        });
    }
    catch (error) {
        if (error instanceof CodexRuntimeError) {
            throw error;
        }
        if (error &&
            typeof error === "object" &&
            "code" in error &&
            typeof error.code === "string" &&
            "errorType" in error &&
            typeof error.errorType === "string" &&
            error instanceof Error) {
            throw new CodexRuntimeError({
                code: error.code,
                errorType: error.errorType,
                message: error.message,
            });
        }
        throw new CodexRuntimeError({
            code: "upstream_unreachable",
            errorType: "adapter",
            message: `upstream_unreachable: ${toErrorMessage(error)}`,
        });
    }
}
async function invokeCodexOnce(input) {
    try {
        return await invokeCodexCliTransport({
            agentControllerPermissionMode: input.agentControllerPermissionMode,
            launchContract: input.launchContract,
            codexTimeoutMs: input.codexTimeoutMs,
            codexWorkdir: input.codexWorkdir,
            conversationPolicy: input.conversationPolicy,
            env: input.env,
            executionMode: input.executionMode,
            ...(input.onProcessControlEvent
                ? { onProcessControlEvent: input.onProcessControlEvent }
                : {}),
            ...(input.onPreview ? { onPreview: input.onPreview } : {}),
            ...(input.onRuntimeEvent ? { onRuntimeEvent: input.onRuntimeEvent } : {}),
            prompt: input.prompt,
            streamingMode: input.streamingMode,
            threadId: input.threadId,
        });
    }
    catch (error) {
        if (error instanceof CodexRuntimeError) {
            throw error;
        }
        if (error &&
            typeof error === "object" &&
            "code" in error &&
            typeof error.code === "string" &&
            "errorType" in error &&
            typeof error.errorType === "string" &&
            error instanceof Error) {
            throw new CodexRuntimeError({
                code: error.code,
                errorType: error.errorType,
                message: error.message,
            });
        }
        throw new CodexRuntimeError({
            code: "upstream_unreachable",
            errorType: "adapter",
            message: `upstream_unreachable: ${toErrorMessage(error)}`,
        });
    }
}
function toErrorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    return String(error);
}
